import { cx } from 'emotion';
import React, { CSSProperties, ReactNode } from 'react';

import { DeviceInfo } from '../../../utils/src';
import {
  backdrop,
  drawer,
  backdropVisibleClassName,
  leftAlign,
  rightAlign,
  panel,
  closedStyleLeft,
  closedStyleRight,
} from './Drawer.css';
import DrawerHeader from './DrawerHeader';
import { DrawerBody, DrawerPanel } from './DrawerPanel';
import { IconType } from './types';

const closedStyleMap = {
  left: closedStyleLeft,
  right: closedStyleRight,
};
const classNameMap = {
  left: leftAlign,
  right: rightAlign,
} as const;

type DrawerProps = {
    open: boolean;
    align: 'left' | 'right';
    showBackdrop?: boolean;
    onBackdropClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    className?: string
    style?: CSSProperties;

    // panel props
    width?: string | number;
    height?: string | number;

    // body props
    children: ReactNode;
    bodyClassName?: string;
    bodyStyle?: CSSProperties;

    // header props
    title?: string;
    showHeader?: boolean;
    drawerBackIcon?: boolean;
    icon?: IconType;
    leftIcon?: IconType;
    onLeftIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    rightIcon?: IconType;
    onRightIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    panelClassName?: string;
    panelStyle?: CSSProperties;
    headerClassName?: string;
    headerStyle?: CSSProperties;
    hideContentOnClose?: boolean;
    'data-e2e'?: string;
}

const Drawer = (props: DrawerProps) => {
  const {
    align,
    open,
    showBackdrop = true,
    onBackdropClick = () => {},
    style,
    className,
    panelClassName,
    panelStyle,
    bodyClassName,
    bodyStyle,
    children,
    showHeader = true,
    drawerBackIcon,
    width,
    height,
    title,
    icon,
    leftIcon,
    onLeftIconClick,
    rightIcon,
    onRightIconClick,
    headerClassName,
    headerStyle,
    hideContentOnClose = true,
    'data-e2e': testId,
  } = props;

  const closedClassName = closedStyleMap[align];

  const alignClassName = classNameMap[align];

  return (
    <div data-e2e={testId} className={cx(drawer, className, alignClassName)} style={style} data-name="drawer">
      {DeviceInfo.isHandheld && showBackdrop && (
        <div
          className={cx(backdrop, { [backdropVisibleClassName]: open })}
          onClick={(e) => {
            e.stopPropagation();
            onBackdropClick(e);
          }}
          data-name="backdrop"
        />
      )}
      <DrawerPanel
        width={width}
        height={height}
        className={cx(panel, panelClassName, { [closedClassName]: !open })}
        style={panelStyle}
        open={open}
        hideContentOnClose={hideContentOnClose}
      >
        {showHeader && (
          <DrawerHeader
            className={headerClassName}
            style={headerStyle}
            title={title}
            drawerBackIcon={drawerBackIcon}
            icon={icon}
            leftIcon={leftIcon}
            rightIcon={rightIcon}
            onLeftIconClick={onLeftIconClick}
            onRightIconClick={onRightIconClick}
          />
        )}
        <DrawerBody
          className={bodyClassName}
          style={bodyStyle}
        >
          {children}
        </DrawerBody>
      </DrawerPanel>
    </div>
  );
};

export default Drawer;
